import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';

import { Anchor, Box, Center, Group, Loader } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconFileTypePdf } from '@tabler/icons-react';
import { dateToDateString } from '@utils/date';

import {
  GetInvoicesRequestParams,
  InvoicesSortBy,
  useGetInvoicesCountQuery,
  useGetInvoicesQuery,
} from '@api/invoices.api';

import { LayoutContextType } from '@components/layout/Layout';
import SearchBar from '@components/SearchBar';
import { Invoice } from '@interfaces/invoices.interface';

export default function Invoices() {
  // ==========================================================================
  // General
  // ==========================================================================
  const [searchParams] = useSearchParams();

  const selectedInvoice = searchParams.get('sel');

  // ==========================================================================
  // State
  // ==========================================================================
  const [filters, setFilters] = useState<GetInvoicesRequestParams>({
    page: +(searchParams.get('page') || 1),
    pageLength: +(searchParams.get('pageLength') || 5),
    searchQuery: searchParams.get('search') || undefined,
    sortBy: 'id',
    sortOrder: 'desc',
  });

  const [searchQuery] = useDebouncedValue(filters.searchQuery, 200, {
    leading: true,
  });

  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<Invoice>>({
    columnAccessor: 'id',
    direction: 'desc',
  });

  useEffect(() => {
    if (
      filters.sortBy === sortStatus.columnAccessor &&
      filters.sortOrder === sortStatus.direction
    ) {
      return;
    }

    setFilters((filters) => ({
      ...filters,
      sortBy: sortStatus.columnAccessor as InvoicesSortBy,
      sortOrder: sortStatus.direction,
    }));
  }, [sortStatus]);

  // ==========================================================================
  // Api
  // ==========================================================================

  // Get initial invoices count
  const {
    data: invoicesCount = { count: 0 },
    isLoading: isLoadingInvoicesCount,
  } = useGetInvoicesCountQuery(searchQuery);

  const { data = [], isLoading } = useGetInvoicesQuery({
    ...filters,
    searchQuery,
  });

  // ==========================================================================
  // Render
  // ==========================================================================
  const { setLayoutProps } = useOutletContext<LayoutContextType>();

  useLayoutEffect(() => {
    setLayoutProps({
      title: 'Fatture',
    });
  }, [setLayoutProps]);

  return isLoading || isLoadingInvoicesCount ? (
    <Center my="xl">
      <Loader />
    </Center>
  ) : (
    <>
      <Group mb="lg">
        <SearchBar
          placeholder="Cerca numero fattura"
          value={filters.searchQuery}
          onChange={(newValue) =>
            setFilters({ ...filters, searchQuery: newValue })
          }
        />
      </Group>
      <DataTable
        striped
        withTableBorder
        noRecordsText="Nessuna fattura trovata"
        minHeight={data.length === 0 ? 200 : undefined}
        columns={[
          {
            accessor: 'date',
            title: 'Data',
            sortable: true,
            render: (record) => (
              <Box fw={(record.id === selectedInvoice && 'bold') || undefined}>
                {dateToDateString(new Date(record.date))}
              </Box>
            ),
          },
          {
            accessor: 'id',
            title: 'Numero fattura',
            sortable: true,
            render: (record) => (
              <Box fw={(record.id === selectedInvoice && 'bold') || undefined}>
                {record.id}
              </Box>
            ),
          },
          {
            accessor: 'url',
            title: 'Pdf',
            render: (record) => (
              <Anchor
                href={record.url}
                target="_blank"
                rel="noopener noreferrer nofollow"
              >
                <IconFileTypePdf />
              </Anchor>
            ),
          },
        ]}
        records={data}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        page={filters.page!}
        recordsPerPage={filters.pageLength!}
        totalRecords={invoicesCount.count}
        paginationText={() => ''}
        recordsPerPageLabel=""
        recordsPerPageOptions={[5, 10, 20, 50]}
        onPageChange={(newPage) => setFilters({ ...filters, page: newPage })}
        onRecordsPerPageChange={(newPageLength) =>
          setFilters({ ...filters, pageLength: newPageLength, page: 1 })
        }
      />
    </>
  );
}
